import { APIErrorCode, SUPPORT_EMAIL } from "constants/index";
import { CARRIER_CHECK_FAILED_MESSAGE } from "utils";

import type { UseCarrierExistsResult } from "hooks/useCarrierExists";
import type {
  CreateShipmentFormState,
  CreateShipmentVisibilityBody,
  DirectBookingAssignmentFormState,
} from "types/shipment";

export function getTimeInputLabel(rp: Partial<RoutePoint>): string {
  switch (rp.stop_operation) {
    case "PICKUP":
      return "Ready Date";
    case "DROPOFF":
      return "Delivery Date";
    default:
      return "Date";
  }
}

export function areRouteTimesValid(
  startTime: string | undefined,
  index?: number,
): string | null {
  const indexText = index !== undefined ? `Route point ${index + 1}:` : "";
  if (startTime == null || startTime === "") {
    return `${indexText} ready date can not be empty.`;
  }

  return null;
}

/*
Strips email addresses out of an API error message so it can be sent to product
analytics. Two posting rejections quote the address the shipper typed for a
private shipment invite, which belongs to a third party rather than to the user
the event is attributed to. Redacting here rather than dropping the message
keeps every other rejection reason legible, and covers any message a future
handler adds without it having to know about analytics.
*/
export function redactEmails(text: string): string {
  return text.replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, "[email]");
}

/*
Numeric fields are held as strings in the form state so typing is not restricted.
The request body carries them as numbers; empty input becomes null. Number()
rejects partial parses like "12abc" or "1,500" outright (parseFloat would read
them as 12 and 1), so junk is never mis-read as a smaller number.
*/
export function parseNumericFormInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : null;
}

export const DIRECT_BOOKING_MESSAGE = {
  QUOTES_ONLY: "Direct bookings can't be quotes-only",
  ONE_EQUIPMENT_TYPE: "Direct bookings take one equipment type",
  DELIVERY_DATE: "Set a delivery date",
  CARRIER_REQUIRED: "Select the carrier you booked or type their email.",
  CARRIER_CHECK_PENDING: "Checking the carrier's email...",
  CARRIER_CHECK_FAILED: CARRIER_CHECK_FAILED_MESSAGE,
  CARRIER_REJECTED: "This carrier can't be booked directly.",
  NOT_IN_NETWORK: "Not in your network yet. They will be added when you post.",
  PRICE_REQUIRED: "Enter the price you agreed with the carrier.",
  PRICE_ABOVE_ZERO: "The booked price must be greater than 0.",
  PRICE_FORMAT: "Enter the price as a number with at most 2 decimals.",
} as const;

// The API takes up to 9 digits, 2 of them decimals: 1234567.89 at most.
const BOOKED_PRICE_PATTERN = /^\d{1,7}(\.\d{1,2})?$/;

const DIRECT_BOOKING_ERROR_MESSAGE: Record<string, string> = {
  [APIErrorCode.DIRECT_BOOKING_ASSIGNEE_NOT_FOUND]:
    "No Evotrux carrier uses this email. Check the address.",
  [APIErrorCode.DIRECT_BOOKING_ASSIGNEE_NOT_CARRIER]:
    "This email belongs to a shipper account. Use the carrier's email.",
  [APIErrorCode.DIRECT_BOOKING_ASSIGNEE_WITHOUT_COMPANY]: `This carrier hasn't finished setting up their company. Contact ${SUPPORT_EMAIL} to book them.`,
  [APIErrorCode.DIRECT_BOOKING_ASSIGNEE_DISABLED]: `This carrier's account is disabled. Contact ${SUPPORT_EMAIL} to book them.`,
  [APIErrorCode.DIRECT_BOOKING_ASSIGNEE_BLOCKED]: `You have blocked this carrier. Unblock them, or contact ${SUPPORT_EMAIL} to book them.`,
};

/*
The message for a DIRECT_BOOKING_* rejection, or null for any other code.
DIRECT_BOOKING_INVALID_SHIPMENT carries its reason in the API message.
*/
export function getDirectBookingErrorMessage(
  code: string | undefined,
  apiMessage: string,
): string | null {
  if (code === undefined) {
    return null;
  }
  if (code === APIErrorCode.DIRECT_BOOKING_INVALID_SHIPMENT) {
    return apiMessage === "" ? null : apiMessage;
  }
  return DIRECT_BOOKING_ERROR_MESSAGE[code] ?? null;
}

// What the form knows about the assignee's email.
export interface DirectBookingAssigneeCheck extends Omit<
  UseCarrierExistsResult,
  "retry"
> {
  inNetwork: boolean;
}

export function getDirectBookingAssigneeError(
  assignment: DirectBookingAssignmentFormState,
  assigneeCheck: DirectBookingAssigneeCheck,
): string | null {
  if (assignment.carrierEmail === "") {
    return DIRECT_BOOKING_MESSAGE.CARRIER_REQUIRED;
  }
  if (assigneeCheck.carrierExists === false) {
    /*
    Checked before the network, because blocking a carrier does not remove them
    from it: a partner the shipper blocked is still in the network and the post
    would be refused. An answer without a code predates the code on
    /carrier-exists: the only rejection it could mean is that no carrier has
    the email.
    */
    return (
      getDirectBookingErrorMessage(
        assigneeCheck.rejectionCode ??
          APIErrorCode.DIRECT_BOOKING_ASSIGNEE_NOT_FOUND,
        "",
      ) ?? DIRECT_BOOKING_MESSAGE.CARRIER_REJECTED
    );
  }
  // A carrier already in the network is assignable until the check says
  // otherwise, so a pending or failed check never blocks posting to one.
  if (assigneeCheck.inNetwork || assigneeCheck.carrierExists === true) {
    return null;
  }
  if (assigneeCheck.error !== null) {
    return DIRECT_BOOKING_MESSAGE.CARRIER_CHECK_FAILED;
  }
  return DIRECT_BOOKING_MESSAGE.CARRIER_CHECK_PENDING;
}

// The properties of CREATE_SHIPMENT_DIRECT_BOOKING_ASSIGNEE_CHECKED.
export interface AssigneeCheckedEvent {
  in_network: boolean;
  // Null when the check failed.
  carrier_exists: boolean | null;
  rejection_code: string | null;
}

interface GetAssigneeCheckedEventProps {
  assigneeEmail: string;
  assigneeCheck: DirectBookingAssigneeCheck;
  // The email the event was last sent for, or null before the first.
  lastTrackedEmail: string | null;
}

/*
The event to send for the assignee's email, or null while there is nothing new
to send. It is sent once per committed email, only once its check has settled,
for a network carrier too: blocking a partner leaves them in the network, so
only the check knows whether they can be booked. useCarrierExists keys its
answer to the email it was asked about, so a settled check always belongs to
assigneeEmail.
*/
export function getAssigneeCheckedEventOrNone(
  props: GetAssigneeCheckedEventProps,
): AssigneeCheckedEvent | null {
  const { assigneeEmail, assigneeCheck, lastTrackedEmail } = props;
  if (assigneeEmail === "" || assigneeEmail === lastTrackedEmail) {
    return null;
  }
  const checkSettled =
    !assigneeCheck.isLoading &&
    (assigneeCheck.carrierExists !== null || assigneeCheck.error !== null);
  if (!checkSettled) {
    return null;
  }
  return {
    in_network: assigneeCheck.inNetwork,
    carrier_exists: assigneeCheck.carrierExists,
    rejection_code: assigneeCheck.rejectionCode,
  };
}

export function getDirectBookingPriceError(
  assignment: DirectBookingAssignmentFormState,
): string | null {
  if (assignment.price.trim() === "") {
    return DIRECT_BOOKING_MESSAGE.PRICE_REQUIRED;
  }
  const price = assignment.price.trim();
  if (!BOOKED_PRICE_PATTERN.test(price)) {
    return DIRECT_BOOKING_MESSAGE.PRICE_FORMAT;
  }
  if (Number(price) <= 0) {
    return DIRECT_BOOKING_MESSAGE.PRICE_ABOVE_ZERO;
  }
  return null;
}

/*
Why a direct booking can't be saved as a template yet, or null. A template may
leave the price out until the rate is agreed, but a price that was entered has
to be one the API accepts.
*/
export function getDirectBookingTemplateError(
  assignment: DirectBookingAssignmentFormState | null,
): string | null {
  if (assignment === null) {
    return null;
  }
  if (assignment.carrierEmail === "") {
    return DIRECT_BOOKING_MESSAGE.CARRIER_REQUIRED;
  }
  if (assignment.price.trim() === "") {
    return null;
  }
  return getDirectBookingPriceError(assignment);
}

export function getDirectBookingEquipmentError(
  equipmentTypes: string[],
): string | null {
  return equipmentTypes.length > 1
    ? DIRECT_BOOKING_MESSAGE.ONE_EQUIPMENT_TYPE
    : null;
}

export function getDirectBookingDestinationError(
  routePoints: EditableRoutePointState[],
): string | null {
  const destination = routePoints[routePoints.length - 1];
  return destination?.schedule_type === "DATETIME"
    ? null
    : DIRECT_BOOKING_MESSAGE.DELIVERY_DATE;
}

export function getDirectBookingShipmentTypeError(
  shipmentType: ShipmentType,
): string | null {
  return shipmentType === "QUOTES_ONLY"
    ? DIRECT_BOOKING_MESSAGE.QUOTES_ONLY
    : null;
}

interface ValidateDirectBookingAssignmentProps {
  formState: CreateShipmentFormState;
  assignment: DirectBookingAssignmentFormState;
  assigneeCheck: DirectBookingAssigneeCheck;
}

// The first rule a shipment posted as a direct booking breaks, or null.
export function validateDirectBookingAssignment(
  props: ValidateDirectBookingAssignmentProps,
): string | null {
  const { formState, assignment, assigneeCheck } = props;
  return (
    getDirectBookingShipmentTypeError(formState.shipmentType) ??
    getDirectBookingAssigneeError(assignment, assigneeCheck) ??
    getDirectBookingPriceError(assignment) ??
    getDirectBookingEquipmentError(formState.equipmentTypes) ??
    getDirectBookingDestinationError(formState.routePoints)
  );
}

/*
The visibility fields of the request body. A shipment posted as a direct booking
sends only the assignment and lets the API derive its visibility; every other
shipment sends the notification fields. Price is null on a template saved before
the rate was agreed.
*/
export function getVisibilityBody(
  formState: CreateShipmentFormState,
): CreateShipmentVisibilityBody {
  const assignment = formState.directBookingAssignment;
  if (assignment !== null) {
    return {
      direct_booking: {
        carrier_email: assignment.carrierEmail,
        price: assignment.price.trim() === "" ? null : assignment.price.trim(),
        currency: assignment.currency,
      },
    };
  }
  return {
    notification_settings_type: formState.notificationSettingsType,
    notification_settings_emails: formState.notificationSetingsEmails,
    notification_settings_partner_groups: formState.partnerGroups,
    network_invite_emails: formState.networkInviteEmails,
  };
}
